import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { portalioCache } from './cacheService';
import { logger } from '@/lib/logger';
import { environment } from '@/config/environment';
import { shouldSkipAuth } from '@/lib/auth';
import { fetchWithSupabaseAuth } from '@/lib/supabaseFetch';
import type {
  PortalioProposal,
  ProposalListResponse,
  AuthenticationResponse,
  PortalioError,
} from '@/types/portalio';
import type { PortalOutboundPortalUser } from '@/types/portalOutbound';

interface PortalioRequestOptions {
  bodyFormat?: 'json' | 'form' | 'multipart';
}

async function parseEdgeFunctionResponse(response: Response, context: string): Promise<any> {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch (error) {
      logger.error(`Failed to parse JSON response from ${context}`, {
        status: response.status,
        error,
      });
      return { error: 'Invalid JSON response from Supabase function' };
    }
  }

  const text = await response.text();

  if (text) {
    logger.error(`Non-JSON response from ${context}`, {
      status: response.status,
      bodyPreview: text.slice(0, 200),
    });
    return { error: text };
  }

  return {};
}

function toPortalAiBuilderTextFileName(name: string): string {
  const safeBase = (name || 'vfloow-bom')
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\.+$/, '');

  if (!safeBase) {
    return 'vfloow-bom.txt';
  }

  return safeBase.toLowerCase().endsWith('.txt') ? safeBase : `${safeBase}.txt`;
}

function getPortalAiContentId(response: any): number | null {
  const candidates = [
    response?.id,
    response?.contentId,
    response?.ContentId,
    response?.projectSpecContentId,
    response?.ProjectSpecContentId,
    response?.item?.id,
    response?.content?.id,
    response?.data?.id,
    response?.result?.id,
  ];

  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return null;
}

function getPortalAiContentItems(response: any): any[] {
  const candidates = [
    response?.items,
    response?.content,
    response?.contents,
    response?.data?.items,
    response?.data?.content,
    response?.data,
    response?.result?.items,
    response?.results,
    response,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
}

function describePortalAiContentItems(items: any[]): string {
  if (!items.length) return '0 item(s)';

  const statuses = items
    .map((item: any) => (typeof item?.status === 'string' ? item.status : null))
    .filter((status: string | null): status is string => Boolean(status));

  return `${items.length} item(s); statuses: ${statuses.length ? statuses.join(', ') : 'none'}`;
}

function summarizePortalResponse(response: unknown): string {
  if (response === undefined) return 'undefined';
  if (response === null) return 'null';

  try {
    const json = JSON.stringify(response);
    return json.length > 500 ? `${json.slice(0, 500)}...` : json;
  } catch {
    return String(response).slice(0, 500);
  }
}

function encodeUtf8Base64(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  const chunkSize = 0x8000;

  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.slice(index, index + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary);
}

function wait(ms: number): Promise<void> {
  return new Promise(resolve => globalThis.setTimeout(resolve, ms));
}

export class PortalioClient {
  private orgId: string;
  private lastTokenCheck: Date | null = null;
  private tokenCheckInterval = 300000; // Check every 5 minutes

  constructor(orgId: string) {
    this.orgId = orgId;
  }

  /**
   * Authenticate with Portal.io using username and password
   */
  async authenticate(username: string, password: string): Promise<AuthenticationResponse> {
    try {
      // Make a direct fetch call with proper headers for Supabase edge function
      // Use environment config for dynamic URL handling in network access
      const SUPABASE_URL = environment.supabase.url;

      if (!SUPABASE_URL) {
        throw new Error('Missing Supabase environment variables. Please check your .env file.');
      }

      const response = await fetchWithSupabaseAuth(
        `${SUPABASE_URL}/functions/v1/portalio-auth`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username,
            password,
            orgId: this.orgId,
          }),
        },
        { requireJwt: !shouldSkipAuth(), preferJwt: true },
      );

      const data = await parseEdgeFunctionResponse(response, 'portalio-auth');

      if (!response.ok) {
        logger.error('Authentication failed:', data);
        const detailMessage =
          typeof data?.details === 'string'
            ? data.details
            : data?.details && typeof data.details === 'object'
              ? JSON.stringify(data.details)
              : undefined;

        return {
          success: false,
          error: detailMessage
            ? `${data?.error || 'Authentication failed'}: ${detailMessage}`
            : data?.error || 'Authentication failed',
        };
      }

      // Ensure we return the proper AuthenticationResponse structure
      return {
        success: data.success ?? true,
        userToken: data.userToken,
        userId: typeof data.userId === 'string' ? data.userId : null,
        userName: typeof data.userName === 'string' ? data.userName : null,
        expiresAt: data.expiresAt,
        error: data.error,
      };
    } catch (error) {
      logger.error('Portal.io authentication error:', error);
      // Return error response instead of throwing
      return {
        success: false,
        error: error.message || 'Authentication failed',
      };
    }
  }

  /**
   * Check and refresh token if needed
   */
  private async checkAndRefreshToken(): Promise<boolean> {
    try {
      // Check if we need to refresh (every 5 minutes max)
      if (
        this.lastTokenCheck &&
        Date.now() - this.lastTokenCheck.getTime() < this.tokenCheckInterval
      ) {
        return true; // Recently checked, skip
      }

      this.lastTokenCheck = new Date();

      // Check current token status
      // SECURITY: Using encrypted credentials via helper function (with local dev fallback)
      const client = await getAuthenticatedSupabaseClient();
      const { data: integration, error } = await client
        .from('portal_integrations')
        .select('token_expiry, connection_status, auto_refresh_enabled, user_token, refresh_token')
        .eq('clerk_org_id', this.orgId)
        .eq('is_active', true)
        .single();

      if (error || !integration) {
        logger.error('Failed to get integration for token check:', error);
        return false;
      }

      // Get decrypted credentials if needed (production uses Vault)
      let userToken = null;
      let refreshToken = null;

      // Try RPC first (for production with Vault encryption)
      try {
        const { data: credentials } = await client.rpc('get_decrypted_portal_credentials', {
          org_id_param: this.orgId,
        });
        if (credentials && credentials.length > 0) {
          userToken = credentials[0].user_token;
          refreshToken = credentials[0].refresh_token;
        }
      } catch (rpcError) {
        logger.warn('RPC get_decrypted_portal_credentials failed (local dev?):', rpcError);
      }

      // LOCAL DEV FALLBACK: Check plain text fields if RPC didn't return tokens
      if (!userToken && integration.user_token) {
        userToken = integration.user_token;
        refreshToken = integration.refresh_token;
      }

      // If no token, can't make API calls
      if (!userToken) {
        await this.updateConnectionStatus('disconnected');
        return false;
      }

      // Portal.io uses username-based authentication that doesn't expire
      // We track expiry for monitoring but don't block requests
      // The actual Portal.io API will return errors if auth fails

      // Token is still valid
      if (integration.connection_status !== 'active') {
        await this.updateConnectionStatus('active');
      }
      return true;
    } catch (error) {
      logger.error('Error checking token:', error);
      return false;
    }
  }

  /**
   * Refresh the Portal.io token using our edge function
   */
  private async refreshToken(): Promise<boolean> {
    try {
      await this.updateConnectionStatus('refreshing');

      // Use our edge function to refresh the token
      // Use environment config for dynamic URL handling in network access
      const SUPABASE_URL = environment.supabase.url;

      if (!SUPABASE_URL) {
        throw new Error('Missing Supabase environment variables. Please check your .env file.');
      }

      const response = await fetchWithSupabaseAuth(
        `${SUPABASE_URL}/functions/v1/portalio-refresh`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orgId: this.orgId,
          }),
        },
        { requireJwt: !shouldSkipAuth(), preferJwt: true },
      );

      const data = await parseEdgeFunctionResponse(response, 'portalio-refresh');

      if (!response.ok || !data.success) {
        logger.error('Token refresh failed:', data);
        throw new Error(data.error || 'Failed to refresh token');
      }

      // Token refreshed successfully:
      // expiresAt: data.expiresAt,
      // refreshCount: data.refreshCount

      // The edge function already updated the database, just update our local status
      await this.updateConnectionStatus('active');
      return true;
    } catch (error) {
      logger.error('Failed to refresh token:', error);
      await this.updateConnectionStatus('expired', error.message);
      return false;
    }
  }

  /**
   * Update connection status in database
   */
  private async updateConnectionStatus(status: string, errorMessage?: string): Promise<void> {
    try {
      const client = await getAuthenticatedSupabaseClient();

      const updateData: any = {
        connection_status: status,
        updated_at: new Date().toISOString(),
      };

      if (errorMessage) {
        updateData.last_error_message = errorMessage;
      }

      if (status === 'active') {
        updateData.last_successful_request = new Date().toISOString();
      }

      await client.from('portal_integrations').update(updateData).eq('clerk_org_id', this.orgId);
    } catch (error) {
      logger.error('Failed to update connection status:', error);
    }
  }

  /**
   * Make a proxied request to Portal.io API
   */
  private async makeRequest<T>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
    body?: any,
    isRetry: boolean = false,
    options: PortalioRequestOptions = {},
  ): Promise<T> {
    try {
      const client = await getAuthenticatedSupabaseClient();
      // Check token validity (Portal.io uses persistent username auth)
      const tokenValid = await this.checkAndRefreshToken();
      if (!tokenValid) {
        throw new Error('Portal.io authentication not configured. Please authenticate.');
      }

      // Use direct fetch with Clerk JWT for authentication
      // Use environment config for dynamic URL handling in network access
      const SUPABASE_URL = environment.supabase.url;

      if (!SUPABASE_URL) {
        throw new Error('Missing Supabase environment variables. Please check your .env file.');
      }

      const proxyPayload: {
        endpoint: string;
        method: 'GET' | 'POST' | 'PUT' | 'DELETE';
        body?: any;
        bodyFormat?: 'json' | 'form' | 'multipart';
        orgId: string;
      } = {
        endpoint,
        method,
        body,
        orgId: this.orgId,
      };

      if (options.bodyFormat && options.bodyFormat !== 'json') {
        proxyPayload.bodyFormat = options.bodyFormat;
      }

      const response = await fetchWithSupabaseAuth(
        `${SUPABASE_URL}/functions/v1/portalio-proxy`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(proxyPayload),
        },
        { requireJwt: !shouldSkipAuth(), preferJwt: true },
      );

      const data = await parseEdgeFunctionResponse(response, 'portalio-proxy');

      if (!response.ok) {
        logger.error('Edge function error:', data);

        // If we get a 401 and haven't retried yet, try refreshing the token
        if (response.status === 401 && !isRetry) {
          // Got 401, attempting to refresh token and retry...

          // SECURITY: Get decrypted credentials to check for refresh token
          const { data: credentials } = await client.rpc('get_decrypted_portal_credentials', {
            org_id_param: this.orgId,
          });

          if (credentials && credentials.length > 0 && credentials[0].refresh_token) {
            const refreshSuccess = await this.refreshToken();
            if (refreshSuccess) {
              // Retry the request once after successful refresh
              // Token refreshed, retrying request...
              return this.makeRequest<T>(endpoint, method, body, true, options);
            }
          }
        }

        const errorParts = [
          data?.error || `Request failed with status ${response.status}`,
          data?.message,
          data?.status ? `status ${data.status}` : null,
          data?.diagnostics ? `diagnostics ${JSON.stringify(data.diagnostics)}` : null,
        ].filter(Boolean);

        throw new Error(errorParts.join(': '));
      }

      // Check if the edge function returned an error
      if (data?.error) {
        logger.error('Portal.io API error from proxy:', data);

        // Check if it's an authentication error and we haven't retried
        if (data.error.includes('authentication') && !isRetry) {
          // Authentication error detected, attempting refresh and retry...

          // SECURITY: Get decrypted credentials to check for refresh token
          const { data: credentials } = await client.rpc('get_decrypted_portal_credentials', {
            org_id_param: this.orgId,
          });

          if (credentials && credentials.length > 0 && credentials[0].refresh_token) {
            const refreshSuccess = await this.refreshToken();
            if (refreshSuccess) {
              return this.makeRequest<T>(endpoint, method, body, true, options);
            }
          }
        }

        const diagnostics = data?.diagnostics
          ? `: diagnostics ${JSON.stringify(data.diagnostics)}`
          : '';
        throw new Error(`${data.error}${diagnostics}`);
      }

      // Update last successful request timestamp
      await this.updateConnectionStatus('active');
      return data;
    } catch (error) {
      logger.error('Portal.io API error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get list of proposals
   */
  async getProposals(
    search?: string,
    page: number = 1,
    pageSize: number = 50,
  ): Promise<ProposalListResponse> {
    const params = new URLSearchParams();
    if (search) params.append('SearchText', search);
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());

    const endpoint = `/public/proposals?${params.toString()}`;
    const data = await this.makeRequest<any>(endpoint);

    // Transform each proposal in the response
    const transformedProposals = (data.proposals || []).map((proposal: any) => ({
      id: proposal.id,
      proposalName: proposal.name || 'Untitled Proposal',
      proposalNumber: proposal.number,
      customerName: proposal.customer
        ? `${proposal.customer.firstName || ''} ${proposal.customer.lastName || ''}`.trim() ||
          proposal.customer.companyName ||
          'Unknown Customer'
        : 'Unknown Customer',
      customerEmail: proposal.customer?.email || '',
      customerPhone: proposal.customer?.contactPhone || '',
      status: proposal.status || 'Draft',
      totalAmount: proposal.total?.proposalTotal || 0,
      subtotal: proposal.total?.subtotal || 0,
      tax: proposal.total?.tax || 0,
      createdDate: proposal.createdDate,
      modifiedDate: proposal.lastModifiedDate || proposal.lastModifiedByUserDate,
    }));

    // Transform Portal.io response to our format
    return {
      proposals: transformedProposals,
      total: data.totalCount || transformedProposals.length,
      page: data.page || page,
      pageSize: data.pageSize || pageSize,
    };
  }

  /**
   * Get detailed proposal by ID (with caching)
   */
  async getProposalDetails(proposalId: number): Promise<PortalioProposal> {
    // Check cache first
    const cached = portalioCache.getProposal(proposalId);
    if (cached) {
      // Using cached proposal data for proposalId
      return cached;
    }

    const endpoint = `/public/proposals/${proposalId}`;
    const data = await this.makeRequest<any>(endpoint);

    // Transform the response to match our PortalioProposal type
    const transformed = this.transformProposal(data);

    // Cache the transformed proposal
    portalioCache.setProposal(proposalId, transformed);

    return transformed;
  }

  async getProposalRaw(proposalId: number) {
    return this.makeRequest<any>(`/public/proposals/${proposalId}`);
  }

  /**
   * Search catalog items
   */
  async searchCatalogItems(searchTerm: string, page: number = 1, pageSize: number = 50) {
    const params = new URLSearchParams();
    params.append('search', searchTerm);
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());

    const endpoint = `/public/catalog/items?${params.toString()}`;
    return this.makeRequest<any>(endpoint);
  }

  async getUsers(): Promise<PortalOutboundPortalUser[]> {
    const data = await this.makeRequest<any>('/public/users');
    const users = Array.isArray(data?.users) ? data.users : Array.isArray(data) ? data : [];

    return users.map((user: any) => ({
      id: Number(user.id),
      firstName: typeof user.firstName === 'string' ? user.firstName : null,
      lastName: typeof user.lastName === 'string' ? user.lastName : null,
      email: typeof user.email === 'string' ? user.email : null,
      permissionGroup: typeof user.permissionGroup === 'string' ? user.permissionGroup : null,
    }));
  }

  async createProposal(input: { salesPersonId: number; name?: string }) {
    const body: Record<string, string | number> = {
      SalesPersonId: input.salesPersonId,
    };

    if (input.name?.trim()) {
      body.Name = input.name.trim();
    }

    return this.makeRequest<any>('/public/proposals', 'POST', body, false, {
      bodyFormat: 'form',
    });
  }

  async updateProposalDescription(proposalId: number, description: string) {
    return this.makeRequest<any>(
      `/public/proposals/${proposalId}/description`,
      'POST',
      { Description: description },
      false,
      { bodyFormat: 'form' },
    );
  }

  async updateProposalInternalNotes(proposalId: number, internalNotes: string) {
    return this.makeRequest<any>(
      `/public/proposals/${proposalId}/internalnotes`,
      'POST',
      { InternalNotes: internalNotes },
      false,
      { bodyFormat: 'form' },
    );
  }

  async addProposalArea(proposalId: number, name: string) {
    return this.makeRequest<any>(
      `/public/proposals/${proposalId}/area`,
      'POST',
      { Name: name },
      false,
      { bodyFormat: 'form' },
    );
  }

  async addProposalAreaOption(proposalId: number, areaId: number) {
    return this.makeRequest<any>(
      `/public/proposals/${proposalId}/area/${areaId}/option`,
      'POST',
      {},
      false,
      { bodyFormat: 'form' },
    );
  }

  async updateAreaOptionInstallerNotes(
    proposalId: number,
    areaOptionId: number,
    internalNotes: string,
  ) {
    return this.makeRequest<any>(
      `/public/proposals/${proposalId}/area-options/${areaOptionId}/installernotes`,
      'POST',
      { InstallerNotes: internalNotes },
      false,
      { bodyFormat: 'form' },
    );
  }

  async uploadAiBuilderTextContent(proposalId: number, name: string, content: string) {
    const uploadEndpoint = `/public/api/proposals/${proposalId}/ai/content`;
    const uploadDisplayName = toPortalAiBuilderTextFileName(name);
    // Keep the multipart filename deliberately simple. Portal's upload validator reports
    // an empty file type when it cannot infer the extension from the file part.
    const uploadFileName = 'vfloow-bom.txt';
    const encodedContent = encodeUtf8Base64(content);
    const fileNames = [
      'files',
      'file',
      'Files',
      'File',
      'Content',
      'content',
      'Upload',
      'upload',
      'FileData',
      'fileData',
      'SourceFile',
      'sourceFile',
    ];
    const documentedFormFields = {
      Name: uploadDisplayName,
      IsMultiChunkUpload: false,
    };
    const postmanJsonFields = {
      proposalId,
      name: uploadDisplayName,
      isMultiChunkUpload: false,
    };
    const uploadFields = {
      proposalId,
      ProposalId: proposalId,
      name: uploadDisplayName,
      Name: uploadDisplayName,
      fileName: uploadFileName,
      FileName: uploadFileName,
      isMultiChunkUpload: false,
      IsMultiChunkUpload: false,
    };
    const textContentFields = {
      ...uploadFields,
      contentType: 'text/plain',
      ContentType: 'text/plain',
      mimeType: 'text/plain',
      MimeType: 'text/plain',
      content,
      Content: content,
      text: content,
      Text: content,
      transcript: content,
      Transcript: content,
      fileContent: content,
      FileContent: content,
      fileData: encodedContent,
      FileData: encodedContent,
      data: content,
      Data: content,
    };
    const uploadAttempts: string[] = [];
    let baselineContentIds = new Set<number>();

    try {
      const existingContent = getPortalAiContentItems(await this.listAiBuilderContent(proposalId));
      baselineContentIds = new Set(
        existingContent
          .map((item: any) => getPortalAiContentId(item))
          .filter((id: number | null): id is number => id !== null),
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String((error as any)?.message);
      uploadAttempts.push(`pre-upload list-content failed: ${message}`);
    }

    const findUploadedContentFromList = async (strategyName: string) => {
      await wait(1500);

      try {
        const listResponse = await this.listAiBuilderContent(proposalId);
        const contentItems = getPortalAiContentItems(listResponse);
        const newContent = contentItems.find((item: any) => {
          const itemId = getPortalAiContentId(item);
          return itemId !== null && !baselineContentIds.has(itemId);
        });
        const matchingContent =
          newContent ||
          contentItems.find((item: any) => {
            const itemName = typeof item?.name === 'string' ? item.name : null;
            return itemName === uploadDisplayName || itemName === uploadFileName;
          }) ||
          (baselineContentIds.size === 0 ? contentItems[0] : null);
        const contentId = getPortalAiContentId(matchingContent);

        if (contentId) {
          return matchingContent && typeof matchingContent === 'object'
            ? { ...matchingContent, id: contentId }
            : { id: contentId };
        }

        uploadAttempts.push(
          `${strategyName} list-content after success returned ${describePortalAiContentItems(contentItems)}`,
        );
      } catch (error) {
        const message = error instanceof Error ? error.message : String((error as any)?.message);
        uploadAttempts.push(`${strategyName} list-content after success failed: ${message}`);
      }

      return null;
    };

    const validateUploadResponse = async (strategyName: string, response: any) => {
      const contentId = getPortalAiContentId(response);

      if (contentId) {
        return response && typeof response === 'object'
          ? { ...response, id: contentId }
          : { id: contentId };
      }

      uploadAttempts.push(
        `${strategyName} returned without content id: ${summarizePortalResponse(response)}`,
      );
      return findUploadedContentFromList(strategyName);
    };

    const tryUpload = async (
      strategyName: string,
      body: any,
      bodyFormat: PortalioRequestOptions['bodyFormat'],
    ) => {
      try {
        const response = await this.makeRequest<any>(
          uploadEndpoint,
          'POST',
          body,
          false,
          bodyFormat ? { bodyFormat } : {},
        );
        return validateUploadResponse(strategyName, response);
      } catch (error) {
        const message = error instanceof Error ? error.message : String((error as any)?.message);
        uploadAttempts.push(`${strategyName} failed: ${message}`);

        if (
          message.includes('status 401') ||
          message.includes('status 403') ||
          message.includes('Portal.io authentication not configured')
        ) {
          throw error;
        }

        return null;
      }
    };

    const documentedFormUpload = await tryUpload(
      'documented form-urlencoded upload',
      documentedFormFields,
      'form',
    );
    if (documentedFormUpload) return documentedFormUpload;

    const postmanJsonUpload = await tryUpload('postman json upload', postmanJsonFields, 'json');
    if (postmanJsonUpload) return postmanJsonUpload;

    const formUpload = await tryUpload('form-urlencoded text content', textContentFields, 'form');
    if (formUpload) return formUpload;

    const jsonUpload = await tryUpload('json text content', textContentFields, 'json');
    if (jsonUpload) return jsonUpload;

    for (const fieldName of fileNames) {
      const multipartUpload = await tryUpload(
        `documented multipart file field "${fieldName}"`,
        {
          fields: documentedFormFields,
          files: [
            {
              fieldName,
              fileName: uploadFileName,
              contentType: 'text/plain',
              content,
            },
          ],
        },
        'multipart',
      );

      if (multipartUpload) return multipartUpload;
    }

    for (const fieldName of fileNames) {
      const multipartUpload = await tryUpload(
        `expanded multipart file field "${fieldName}"`,
        {
          fields: uploadFields,
          files: [
            {
              fieldName,
              fileName: uploadFileName,
              contentType: 'text/plain',
              content,
            },
          ],
        },
        'multipart',
      );

      if (multipartUpload) return multipartUpload;
    }

    const attemptSummary = uploadAttempts.join(' | ').slice(0, 6000);
    throw new Error(
      `Portal AI Builder upload did not create a content item. Upload filename: ${uploadFileName}; display name: ${uploadDisplayName}. Attempts: ${attemptSummary}`,
    );
  }

  async listAiBuilderContent(proposalId: number) {
    return this.makeRequest<any>(`/public/api/proposals/${proposalId}/ai/content`);
  }

  async generateAiOutline(proposalId: number) {
    return this.makeRequest<any>(
      `/public/proposals/${proposalId}/ai/outline`,
      'POST',
      { proposalId },
      false,
      { bodyFormat: 'json' },
    );
  }

  async getAiOutline(proposalId: number) {
    return this.makeRequest<any>(`/public/api/proposals/${proposalId}/ai/outline`);
  }

  async buildAiProposal(proposalId: number) {
    return this.makeRequest<any>(
      `/public/proposals/${proposalId}/ai/build`,
      'POST',
      { proposalId },
      false,
      { bodyFormat: 'json' },
    );
  }

  /**
   * Get proposal change orders
   */
  async getChangeOrders(proposalId: number) {
    const endpoint = `/public/proposal/${proposalId}/changeorders`;
    return this.makeRequest<any>(endpoint);
  }

  /**
   * Transform Portal.io proposal to our format
   */
  private transformProposal(data: any): PortalioProposal {
    // Handle customer name
    const customerName = data.customer
      ? `${data.customer.firstName || ''} ${data.customer.lastName || ''}`.trim() ||
        data.customer.companyName ||
        'Unknown Customer'
      : 'Unknown Customer';

    return {
      id: data.id,
      proposalName: data.name || 'Untitled Proposal',
      proposalNumber: data.number,
      customerName: customerName,
      customerEmail: data.customer?.contactEmail || '',
      customerPhone: data.customer?.contactPhone || '',
      status: data.status || 'Draft',
      totalAmount: data.financialSummary?.proposalTotal || 0,
      subtotal: data.financialSummary?.proposalSubtotal || 0,
      tax: data.financialSummary?.salesTax?.total || 0,
      createdDate: data.createdDate,
      modifiedDate: data.lastModifiedDate || data.lastModifiedByUserDate,
      areas: this.transformAreas(data.areas || []),
    };
  }

  /**
   * Transform areas data
   */
  private transformAreas(areas: any[]): any[] {
    return areas.map(area => ({
      id: area.id,
      areaName: area.name || 'Unnamed Area',
      description: area.description,
      sortOrder: area.sortOrder || 0,
      options: this.transformOptions(area.options || []),
    }));
  }

  /**
   * Transform options data
   */
  private transformOptions(options: any[]): any[] {
    return options.map((option, index) => ({
      id: option.id,
      optionName: option.clientDescription || option.installerDescription || `Option ${index + 1}`,
      optionNumber: option.number,
      isSelected: option.status === 'Selected' || option.status === 'Approved',
      price: option.total || 0,
      items: this.transformItems(option.items || []),
    }));
  }

  /**
   * Transform items data
   */
  private transformItems(items: any[]): any[] {
    return items.map(item => ({
      id: item.id,
      brand: item.brand || '',
      model: item.model || '',
      partNumber: item.sku || item.partNumber,
      description: item.description || '',
      name: item.name || item.description || '',
      category: item.itemType || 'Part',
      quantity: item.quantity || 1,
      unitPrice: item.cost || 0,
      sellPrice: item.sellPrice || 0,
      totalPrice: item.total?.amount || item.sellPrice * item.quantity || 0,
      imageUrl: item.imageUrl || '',
      specifications: item.specifications || {},
      // Add referencedItemId - this is Portal's universal product identifier
      referenceId: item.referencedItemId ? String(item.referencedItemId) : null,
    }));
  }

  /**
   * Handle and format errors
   */
  private handleError(error: any): PortalioError {
    if (error.code === 'PORTAL_AUTH_EXPIRED') {
      return {
        code: 'AUTH_EXPIRED',
        message: 'Portal.io authentication expired. Please re-authenticate.',
      };
    }

    if (error.code === 'PORTAL_NOT_CONFIGURED') {
      return {
        code: 'NOT_CONFIGURED',
        message: 'Portal.io integration is not configured. Please set up your credentials.',
      };
    }

    return {
      code: 'UNKNOWN_ERROR',
      message: error.message || 'An unexpected error occurred',
      details: error,
    };
  }
}

// Factory function to create client instance
export function createPortalioClient(orgId: string): PortalioClient {
  return new PortalioClient(orgId);
}
